import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { api } from '../../../Components/functions/Api';
import { toast } from 'react-toastify';

/**
 * ChatRedirect Component
 * 
 * Handles deep linking from email notifications to specific chat conversations.
 * 
 * Flow:
 * 1. Extract chatId from URL parameter (/chat/:chatId)
 * 2. Fetch chat details to determine user's role (host/traveler)
 * 3. Redirect to appropriate inbox with chat parameter
 * 4. Ensure user is logged in before accessing chat
 */

export const ChatRedirect = () => {
  const { chatId } = useParams();
  const navigate = useNavigate();
  const account = useSelector((state) => state.auth.account);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleChatRedirect = async () => {
      // Check if user is logged in
      if (!account) {
        console.log('⚠️ User not logged in, redirecting to home');
        toast.info('Please connect your wallet to view messages');
        navigate('/');
        return;
      }

      if (!chatId) {
        console.error('❌ No chatId provided');
        toast.error('Invalid chat link');
        navigate('/');
        return;
      }

      try {
        console.log('🔍 Fetching chat details for:', chatId);

        // Fetch chat info to determine user's role
        const chatInfo = await api('chat/getChattingInfo', {
          id: chatId,
          reader: account
        });

        if (!chatInfo) {
          console.error('❌ Chat not found:', chatId);
          toast.error('Chat conversation not found');
          navigate('/');
          return;
        }

        console.log('✅ Chat details fetched:', chatInfo);

        // Determine user's role
        // If current user is the owner → host
        // If current user is the renter → traveler/tenant
        const isOwner = chatInfo.owner === account;
        const isRenter = chatInfo.renter === account;

        let redirectPath = '';
        
        if (isOwner) {
          // User is the property owner → host inbox
          redirectPath = `/dashboard/host/inbox?chat=${chatId}`;
        } else if (isRenter) {
          // User is the renter → traveler inbox
          redirectPath = `/dashboard/traveler/inbox?chat=${chatId}`;
        } else {
          // Fallback: user is neither (shouldn't happen, but handle gracefully)
          console.error('❌ User is neither owner nor renter of this chat');
          toast.error('You do not have access to this conversation');
          navigate('/dashboard/traveler/inbox');
          return;
        }

        console.log('📍 Redirecting to:', redirectPath, {
          account,
          isOwner,
          isRenter,
          owner: chatInfo.owner,
          renter: chatInfo.renter
        });
        navigate(redirectPath, { replace: true });

      } catch (error) {
        console.error('💥 Error fetching chat details:', error);
        setError('Failed to load chat. Please try again.');
        toast.error('Failed to load chat conversation');
        
        // Redirect to inbox after 2 seconds
        setTimeout(() => {
          navigate('/dashboard/traveler/inbox');
        }, 2000);
      } finally {
        setIsLoading(false);
      }
    };

    handleChatRedirect();
  }, [chatId, account, navigate]);

  if (isLoading) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-[#fafafa]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg font-medium">Loading your conversation...</p>
          <p className="text-gray-400 text-sm mt-2">Please wait while we open your chat</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-[#fafafa]">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Oops! Something went wrong</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/dashboard/traveler/inbox')}
            className="bg-purple-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors"
          >
            Go to Inbox
          </button>
        </div>
      </div>
    );
  }

  return null;
};

